import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { CartContext } from "./CartContext";

/* Hook : détecte la direction d'entrée/sortie de la souris pour
   l'effet de soulignement directionnel (origin left/right). */
function useDirectionalHover() {
  const handleMouseEnter = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const fromLeft = e.clientX - rect.left < rect.width / 2;
    e.currentTarget.style.setProperty("--ul-origin", fromLeft ? "left" : "right");
    e.currentTarget.classList.add("is-hovered");
  }, []);

  const handleMouseLeave = useCallback((e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const exitRight = e.clientX - rect.left > rect.width / 2;
    e.currentTarget.style.setProperty("--ul-origin", exitRight ? "right" : "left");
    e.currentTarget.classList.remove("is-hovered");
  }, []);

  return { onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave };
}

const NavLink = ({ to, isActive, children }) => {
  const hover = useDirectionalHover();
  return (
    <Link
      to={to}
      className={`site-nav-link${isActive ? " is-active" : ""}`}
      {...hover}
    >
      {children}
    </Link>
  );
};

const Navbar = () => {
  const { cartItems } = useContext(CartContext);
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 860);
  const [scrolled, setScrolled] = useState(false);

  const isLoggedIn = useMemo(() => !!localStorage.getItem("user"), []);
  const cartCount = cartItems?.length || 0;

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth <= 860;
      setIsMobile(mobile);
      if (!mobile) setOpen(false);
    };
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const p = location.pathname;

  return (
    <header className={`site-navbar${scrolled || open ? " is-scrolled" : ""}`}>
      <div className="container site-navbar-inner">

        <div className="site-navbar-left">
          {isMobile && (
            <button
              className="button button-secondary nav-burger"
              onClick={() => setOpen((v) => !v)}
              aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
              aria-expanded={open}
            >
              {open ? "✕" : "☰"}
            </button>
          )}
          <Link to="/" aria-label="Aller à l'accueil" className="site-logo-link">
            <img
              src="/images/logo.svg"
              alt="Deenzine"
              className="site-logo"
              loading="eager"
              onError={(e) => { e.currentTarget.src = "/images/logo.png"; }}
            />
          </Link>
        </div>

        {!isMobile && (
          <nav aria-label="Navigation principale" className="site-nav-pill">
            <ul>
              <li><NavLink to="/"           isActive={p === "/"}>Accueil</NavLink></li>
              <li><NavLink to="/products"   isActive={p.startsWith("/products")}>Collection</NavLink></li>
              <li><NavLink to="/destockage" isActive={p.startsWith("/destockage")}>Déstockage</NavLink></li>
              <li><NavLink to="/contact"    isActive={p.startsWith("/contact")}>Contact</NavLink></li>
            </ul>
          </nav>
        )}

        <div className="site-navbar-actions">
          <Link className="button button-secondary nav-action" to={isLoggedIn ? "/account" : "/login"}>
            {isLoggedIn ? "Mon compte" : "Connexion"}
          </Link>
          <Link className="button nav-action" to="/cart">
            Panier
            {cartCount > 0 && <span className="cart-count">{cartCount}</span>}
          </Link>
        </div>
      </div>

      {isMobile && (
        <div className={`site-mobile-menu${open ? " is-open" : ""}`}>
          <div className="container">
            <nav aria-label="Navigation mobile">
              <Link className="button button-secondary" to="/">Accueil</Link>
              <Link className="button button-secondary" to="/products">Collection</Link>
              <Link className="button button-secondary" to="/destockage">Déstockage</Link>
              <Link className="button button-secondary" to="/contact">Contact</Link>
            </nav>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;